import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase/config';

// ─── Confirmation modal for removing participants ────────────────────────────

const RemoveConfirmModal = ({ participants, eventId, onCancel, onConfirm, loading }) => (
  <div
    onClick={onCancel}
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10005, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 0.2s ease-out' }}
  >
    <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', padding: '24px', maxWidth: '400px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', animation: 'slideUp 0.3s ease-out' }}>
      <div style={{ width: '56px', height: '56px', background: '#fef7f7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ea4335" strokeWidth="2" strokeLinecap="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </div>
      <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 600, color: '#202124', textAlign: 'center' }}>Remove Participants?</h3>
      <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#5f6368', textAlign: 'center', lineHeight: 1.5 }}>
        The following {participants.length} participant{participants.length > 1 ? 's' : ''} will be removed:
      </p>
      <div style={{ maxHeight: '200px', overflowY: 'auto', background: '#f8f9fa', borderRadius: '8px', padding: '12px', marginBottom: '24px' }}>
        {participants.map(p => (
          <div key={p.id} style={{ padding: '8px 0', borderBottom: '1px solid #e0e0e0', fontSize: '14px', color: '#202124', fontWeight: 500 }}>
            • {p.name}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={onConfirm}
          disabled={loading}
          onMouseOver={(e) => { if (!loading) e.currentTarget.style.background = '#d33426'; }}
          onMouseOut={(e) => { e.currentTarget.style.background = '#ea4335'; }}
          style={{ flex: 1, padding: '12px', background: '#ea4335', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, transition: 'all 0.2s' }}
        >
          {loading ? 'Removing...' : 'Confirm'}
        </button>
        <button
          onClick={onCancel}
          disabled={loading}
          onMouseOver={(e) => { if (!loading) e.currentTarget.style.background = '#f1f3f4'; }}
          onMouseOut={(e) => { e.currentTarget.style.background = '#f8f9fa'; }}
          style={{ flex: 1, padding: '12px', background: '#f8f9fa', color: '#5f6368', border: '2px solid #dadce0', borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
        >
          Cancel
        </button>
      </div>
    </div>
    <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes slideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
  </div>
);

// ─── main component ──────────────────────────────────────────────────────────

const AllParticipantsPanel = () => {
  const [panel, setPanel] = useState(null);
  // panel = { eventData, participantsData }
  const [visible, setVisible] = useState(false);
  const [removeMode, setRemoveMode] = useState(false);
  const [selected, setSelected] = useState(new Set()); // Set of { id, name }
  const [showConfirm, setShowConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    const handleOpen = (e) => {
      setPanel(e.detail);
      setRemoveMode(false);
      setSelected(new Set());
      setShowConfirm(false);
      setVisible(false);
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    };
    const handleClose = () => {
      setVisible(false);
      setTimeout(() => { setPanel(null); setRemoveMode(false); setSelected(new Set()); }, 300);
    };

    window.addEventListener('openAllParticipantsPanel', handleOpen);
    window.addEventListener('closeAllParticipantsPanel', handleClose);
    window.closeAllParticipantsPanel = handleClose;

    return () => {
      window.removeEventListener('openAllParticipantsPanel', handleOpen);
      window.removeEventListener('closeAllParticipantsPanel', handleClose);
    };
  }, []);

  if (!panel) return null;

  const { eventData, participantsData } = panel;
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  const isOrganizer = eventData.creatorId === currentUserId;

  const toggleSelect = (participant) => {
    if (participant.isOrganizer) return;
    setSelected(prev => {
      const next = new Set(prev);
      const existing = [...next].find(p => p.id === participant.id);
      if (existing) next.delete(existing);
      else next.add({ id: participant.id, name: participant.name });
      return next;
    });
  };

  const isSelected = (id) => [...selected].some(p => p.id === id);

  const handleConfirmRemove = async () => {
    if (removing) return;
    setRemoving(true);
    const selectedArr = [...selected];
    try {
      const supabaseClient = window.supabase || supabase;
      const { data: event, error: fetchErr } = await supabaseClient
        .from('events').select('participants, title').eq('event_id', eventData.eventId).single();
      if (fetchErr) throw fetchErr;

      const idsToRemove = selectedArr.map(p => p.id);
      const updated = event.participants.filter(id => !idsToRemove.includes(id));
      const { error: updateErr } = await supabaseClient
        .from('events').update({ participants: updated }).eq('event_id', eventData.eventId);
      if (updateErr) throw updateErr;

      for (const p of selectedArr) {
        await supabaseClient.from('notifications').insert({
          user_id: p.id, type: 'removed_from_event',
          title: 'Removed from Event',
          message: `You were removed from "${event.title}"`,
          event_id: eventData.eventId, event_title: event.title
        });
      }

      if (window.updateNotificationCount) window.updateNotificationCount();
      if (window.updateEventDetailsPanel) setTimeout(() => window.updateEventDetailsPanel(eventData.eventId), 200);
      if (window.loadUserEvents) setTimeout(() => window.loadUserEvents(), 400);
      if (window.refreshSingleEventMarker) window.refreshSingleEventMarker(eventData.eventId);

      window.showToast?.(`${selectedArr.length} participant${selectedArr.length > 1 ? 's' : ''} removed successfully`, 'success');
      setShowConfirm(false);
      setVisible(false);
      setTimeout(() => setPanel(null), 300);
    } catch {
      window.showToast?.('Failed to remove participants. Please try again.', 'error');
    } finally {
      setRemoving(false);
    }
  };

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => { setPanel(null); setRemoveMode(false); setSelected(new Set()); }, 300);
  };

  return (
    <>
      {/* Panel */}
      <div
        data-slide-panel="right"
        style={{
          position: 'fixed', top: '79px',
          right: visible ? '0' : '-400px',
          width: '380px', maxWidth: 'calc(100vw - 4px)', height: 'calc(100vh - 79px - var(--footer-height, 50px))',
          background: 'white', boxShadow: '-4px 0 20px rgba(0,0,0,0.08)',
          borderBottomLeftRadius: "20px",
          borderTopLeftRadius: "20px",
          zIndex: 10003,
          transition: 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflowY: 'auto', WebkitOverflowScrolling: 'touch',
          fontFamily: "'Outfit', sans-serif",
          display: 'flex', flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Back button */}
        <button
          onClick={removeMode ? () => { setRemoveMode(false); setSelected(new Set()); } : handleClose}
          onMouseOver={(e) => e.currentTarget.style.background = '#f1f3f4'}
          onMouseOut={(e) => e.currentTarget.style.background = 'white'}
          style={{ position: 'absolute', top: '15px', left: '20px', width: '40px', height: '40px', borderRadius: '50%', background: 'white', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', transition: 'background 0.2s' }}
        >
          {removeMode ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#202124" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#202124" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          )}
        </button>

        {/* Header */}
        <div style={{ padding: '20px 60px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', flexShrink: 0 }}>
          <h2 style={{ fontSize: '20px', fontWeight: 500, color: '#202124', margin: 0 }}>
            {removeMode ? 'Remove Participants' : `Participants (${participantsData.length})`}
          </h2>
{isOrganizer && !removeMode && !eventData.archived && (
            <button
              onClick={() => setRemoveMode(true)}
              title="Remove participants"
              style={{ position: 'absolute', right: '24px', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ea4335" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="8.5" cy="7" r="4" />
                <line x1="23" y1="11" x2="17" y2="11" />
              </svg>
            </button>
          )}
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {participantsData.map(participant => {
            const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(participant.name || 'User')}&background=e8eaed&color=5f6368&size=48`;
            let borderColor = participant.isCurrentUser ? '#00a3b7' : participant.isOnline ? '#34a853' : '#9e9e9e';
            const statusColor = participant.isOnline ? '#34a853' : '#9e9e9e';
            const sel = isSelected(participant.id);

            return (
              <div
                key={participant.id}
                onClick={() => {
                  if (removeMode) { toggleSelect(participant); return; }
                  window.userProfileEventData = eventData;
                  window.showUserProfilePanel?.(participant.id, 'participants');
                }}
                onMouseOver={(e) => { if (!removeMode) e.currentTarget.style.background = '#f1f3f4'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = participant.isOrganizer ? '#f8f9fa' : 'transparent'; }}
                style={{
                  padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '12px',
                  cursor: removeMode && participant.isOrganizer ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s ease',
                  background: participant.isOrganizer ? '#f8f9fa' : 'transparent'
                }}
              >
                {/* Checkbox in remove mode */}
                {removeMode && (
                  <input
                    type="checkbox"
                    checked={sel}
                    disabled={participant.isOrganizer}
                    onChange={() => toggleSelect(participant)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: '20px', height: '20px', cursor: participant.isOrganizer ? 'not-allowed' : 'pointer', accentColor: '#ea4335', margin: 0, flexShrink: 0, opacity: participant.isOrganizer ? 0.3 : 1 }}
                  />
                )}

                {/* Avatar */}
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <img
                    src={participant.profile_picture || fallbackAvatar}
                    alt={participant.name}
                    style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', border: `2px solid ${borderColor}` }}
                    onError={(e) => { e.target.src = fallbackAvatar; }}
                  />
                  <div style={{ position: 'absolute', bottom: 0, right: 0, width: '14px', height: '14px', background: statusColor, border: '2px solid white', borderRadius: '50%', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
                </div>

                {/* Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: '#202124', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {participant.name}
                    {participant.isOrganizer && (
                      <span style={{ fontSize: '10px', padding: '2px 6px', background: '#00a3b7', color: 'white', borderRadius: '4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Organizer
                      </span>
                    )}
                  </div>
                </div>

                {/* Arrow (normal mode) */}
                {!removeMode && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="2">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>

        {/* Remove footer */}
        {removeMode && (
          <div style={{ position: 'sticky', bottom: 0, padding: '16px 24px', borderTop: '1px solid #e0e0e0', background: 'white', boxShadow: '0 -2px 8px rgba(0,0,0,0.1)', flexShrink: 0 }}>
            <button
              onClick={() => selected.size > 0 && setShowConfirm(true)}
              disabled={selected.size === 0}
              onMouseOver={(e) => { if (selected.size > 0) e.currentTarget.style.background = '#fef7f7'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = 'white'; }}
              style={{
                width: '100%', padding: '14px', background: 'white', color: '#ea4335',
                border: '2px solid #ea4335', borderRadius: '8px', fontSize: '15px', fontWeight: 600,
                cursor: selected.size === 0 ? 'not-allowed' : 'pointer',
                opacity: selected.size === 0 ? 0.5 : 1, transition: 'all 0.2s'
              }}
            >
              Remove Selected ({selected.size})
            </button>
          </div>
        )}
      </div>

      {/* Remove confirmation */}
      {showConfirm && (
        <RemoveConfirmModal
          participants={[...selected]}
          eventId={eventData.eventId}
          onCancel={() => setShowConfirm(false)}
          onConfirm={handleConfirmRemove}
          loading={removing}
        />
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </>
  );
};

export default AllParticipantsPanel;